// PlayProfilePanel — selettore "Profili di Gioco" + Tachimetro adattivo +
// master switch (attiva/disattiva la modalità) + tooltip esplicativo.
//
// L'algoritmo del Tachimetro è "consistent-by-design":
//   • 55% del punteggio è una BASE GLOBALE comune a tutti i profili,
//     costruita su due metriche già calcolate dal backend e quindi solide:
//       - 40% ``affidabilita.score`` (0-100): combina convergence,
//         dominance e qualità del Target Kitano.
//       - 15% ``convergence.score`` (0-100): quanto i 3 modelli
//         (Poisson, ELO, Form) concordano sull'esito.
//   • 45% è IL SEGNALE SPECIFICO DEL PROFILO (vedi computeProfileScore).
//
// Conseguenze utili:
//   - Una partita con affidabilità globale bassa NON può mai diventare un
//     pronostico "Eccellente" in nessun profilo: il tetto è ~5.5/10.
//   - Una partita con affidabilità alta ma senza value bets avrà
//     score alto in Conservatore e medio-basso in Value Hunter — è
//     esattamente il comportamento atteso.
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Shield, Diamond, Layers, Target, Info, ToggleLeft, ToggleRight, X } from "lucide-react";

export const PROFILES = [
  {
    id: "conservatore",
    label: "Conservatore",
    short: "Sicurezza",
    color: "#22FFA3",
    icon: Shield,
    desc: "Favorita chiara, doppia chance, basso rischio.",
    explain: {
      goal: "Massimizzare la probabilità di vincere il singolo pronostico, anche a costo di quote più basse.",
      panels: "Mostra in cima: Pronostico 1X2, Doppia Chance, segnali di rischio (red flag, hot keeper), distribuzione gol.",
      formula: [
        "55% Base globale (affidabilità + convergenza modelli)",
        "30% Probabilità della favorita 1X2",
        "15% Migliore Doppia Chance disponibile",
      ],
    },
  },
  {
    id: "value",
    label: "Value Hunter",
    short: "Edge",
    color: "#F6C643",
    icon: Diamond,
    desc: "Quote sottostimate, edge ML, value bets.",
    explain: {
      goal: "Trovare le partite in cui il bookmaker offre quote superiori alla probabilità reale stimata dal modello (edge positivo).",
      panels: "Mostra in cima: quote con Edge, metriche avanzate xG, Monte Carlo, heatmap.",
      formula: [
        "55% Base globale (affidabilità + convergenza modelli)",
        "25% Numero di tag Value/Premium sui mercati",
        "20% Edge medio % rispetto alle quote del bookmaker",
      ],
    },
  },
  {
    id: "combo",
    label: "Combo Builder",
    short: "Combo",
    color: "#9DF6FF",
    icon: Layers,
    desc: "Over / BTTS / Doppia Chance per la multipla.",
    explain: {
      goal: "Costruire schedine multiple combinando mercati gol (Over/BTTS) e doppie chance ad alta probabilità.",
      panels: "Mostra in cima: distribuzione gol per squadra, metriche avanzate, Gundam Score, quote.",
      formula: [
        "55% Base globale (affidabilità + convergenza modelli)",
        "20% Probabilità Over 2.5",
        "15% Probabilità Gol/Gol (BTTS)",
        "10% Numero di mercati con probabilità ≥ 60%",
      ],
    },
  },
  {
    id: "exact",
    label: "Exact Score",
    short: "Risultato",
    color: "#FF6FD8",
    icon: Target,
    desc: "Risultati esatti, heatmap, range gol.",
    explain: {
      goal: "Pronosticare il risultato esatto della partita o un range di risultati molto stretto.",
      panels: "Mostra in cima: Gundam Score (top 3 risultati esatti), heatmap 5x5, Monte Carlo, metriche xG.",
      formula: [
        "55% Base globale (affidabilità + convergenza modelli)",
        "25% Probabilità del risultato esatto #1 (Gundam)",
        "20% Concentrazione probabilistica dei Top 3 risultati",
      ],
    },
  },
];

const STORAGE_KEY_PROFILE = "bc_play_profile";
const STORAGE_KEY_ENABLED = "bc_play_profile_enabled";

export function loadActiveProfile() {
  try {
    const v = localStorage.getItem(STORAGE_KEY_PROFILE);
    if (v && PROFILES.some((p) => p.id === v)) return v;
  } catch { /* ignore */ }
  return "conservatore";
}
export function saveActiveProfile(id) {
  try { localStorage.setItem(STORAGE_KEY_PROFILE, id); } catch { /* ignore */ }
}
export function loadProfileEnabled() {
  try {
    const v = localStorage.getItem(STORAGE_KEY_ENABLED);
    if (v === "0" || v === "false") return false;
  } catch { /* ignore */ }
  return true;
}
export function saveProfileEnabled(enabled) {
  try { localStorage.setItem(STORAGE_KEY_ENABLED, enabled ? "1" : "0"); } catch { /* ignore */ }
}

export function getProfile(id) {
  return PROFILES.find((p) => p.id === id) || PROFILES[0];
}

// ─── Algoritmo Tachimetro ─────────────────────────────────────────
//
// ``norm(v, lo, hi)`` mappa ``v ∈ [lo, hi]`` su ``[0, 10]`` con clamping
// agli estremi. ``lo`` rappresenta il "pavimento" sotto cui il segnale
// è inservibile, ``hi`` il "soffitto" sopra cui non si guadagna altro.
function norm(v, lo, hi) {
  if (v == null || isNaN(v)) return 0;
  const x = (Number(v) - lo) / (hi - lo);
  return Math.max(0, Math.min(10, x * 10));
}

function tagOf(b) {
  const t = b?.tag;
  if (!t) return "";
  if (typeof t === "string") return t.toLowerCase();
  return String(t.tag || "").toLowerCase();
}

// Punteggio finale 0-10 e breakdown per UI.
// ``predictions`` può venire dal payload /api/match (completo) OPPURE dal
// payload /api/predictions-summary (più snello: senza heatmap e monte_carlo).
// Il fallback è sempre 0, mai null/NaN.
export function computeProfileScore(profileId, predictions) {
  const empty = { score: 0, breakdown: [], baseScore: 0 };
  if (!predictions) return empty;

  const combined = predictions.combined || {};
  const mg = predictions.multigol || {};
  const value = predictions.value_data || predictions.value || {};
  const bets = predictions.recommended_bets || [];
  const gundam = predictions.gundam_scores || [];
  // ``heatmap_5x5`` esiste solo nel payload /api/match, NON in
  // /predictions-summary. Lo manteniamo qui solo per il debug visivo
  // del breakdown locale ma il punteggio FINALE viene preso da
  // ``profile_scores`` (computato server-side) quando disponibile, così
  // il numero mostrato in MatchCard (HomePage) e MatchPage è IDENTICO.

  // ── BASE GLOBALE (55%) — identica per tutti i profili ───────────
  const affScore = Number(predictions?.affidabilita?.score ?? 0); // 0-100
  const convScore = Number(predictions?.convergence?.score ?? 0); // 0-100
  const base = 0.40 * (affScore / 10) + 0.15 * (convScore / 10);
  const baseBreakdown = [
    { label: "Affidabilità globale", value: `${affScore.toFixed(0)}/100`, weight: "40%" },
    { label: "Convergenza modelli", value: `${convScore.toFixed(0)}/100`, weight: "15%" },
  ];

  // Score finale (server-side se disponibile, altrimenti calcolo locale)
  const serverScores = predictions.profile_scores || null;
  const serverScore = serverScores && typeof serverScores[profileId] === "number"
    ? Number(serverScores[profileId])
    : null;

  // ── SEGNALE SPECIFICO DEL PROFILO (45%) ─────────────────────────
  if (profileId === "conservatore") {
    // ``combined`` usa ``home`` / ``draw`` / ``away`` (non home_win).
    const probs = [
      Number(combined.home || 0),
      Number(combined.draw || 0),
      Number(combined.away || 0),
    ].sort((a, b) => b - a);
    const maxP = probs[0];
    const dcMax = Math.max(
      Number(combined.double_1x || 0),
      Number(combined.double_x2 || 0),
      Number(combined.double_12 || 0),
    );
    const sMax = norm(maxP, 35, 70);   // 0-10
    const sDc = norm(dcMax, 60, 88);   // 0-10
    const profileScore = 0.30 * sMax + 0.15 * sDc;
    const localScore = Math.max(0, Math.min(10, base + profileScore));
    return {
      score: serverScore != null ? serverScore : localScore,
      baseScore: base,
      breakdown: [
        ...baseBreakdown,
        { label: "Prob. favorita 1X2", value: `${maxP.toFixed(1)}%`, weight: "30%" },
        { label: "Miglior Doppia Chance", value: `${dcMax.toFixed(1)}%`, weight: "15%" },
      ],
    };
  }

  if (profileId === "value") {
    const valueBets = bets.filter((b) => {
      const t = tagOf(b);
      return t === "value" || t === "premium";
    });
    const premiumCount = valueBets.filter((b) => tagOf(b) === "premium").length;
    const count = valueBets.length;
    // value/value_data: oggetto { market_key: {value_pct, has_value, ...} }
    const edges = Object.values(value || {})
      .map((v) => (v && typeof v.value_pct === "number" ? v.value_pct : null))
      .filter((v) => v != null && v > 0);
    const avgEdge = edges.length ? edges.reduce((a, b) => a + b, 0) / edges.length : 0;
    // Conta i Premium 2× perché sono il segnale più forte
    const taggedSignal = count + premiumCount;
    const sTags = norm(taggedSignal, 0, 7); // 0..10
    const sEdge = norm(avgEdge, 2, 14);     // 0..10
    const profileScore = 0.25 * sTags + 0.20 * sEdge;
    const localScore = Math.max(0, Math.min(10, base + profileScore));
    return {
      score: serverScore != null ? serverScore : localScore,
      baseScore: base,
      breakdown: [
        ...baseBreakdown,
        { label: "Tag Value/Premium", value: `${count} (${premiumCount} 💎)`, weight: "25%" },
        { label: "Edge medio mercato", value: `+${avgEdge.toFixed(1)}%`, weight: "20%" },
      ],
    };
  }

  if (profileId === "combo") {
    const over25 = Number(combined.over25 || 0);
    const btts = Number(combined.btts_yes || 0);
    const candidates = [
      combined.over15, combined.over25, combined.over35,
      combined.btts_yes, combined.double_1x, combined.double_x2, combined.double_12,
      mg.home_1_3, mg.away_1_3, mg.home_2_4, mg.away_2_4,
    ].map((x) => Number(x || 0));
    const strongCount = candidates.filter((x) => x >= 60).length;
    const sOver = norm(over25, 45, 75);
    const sBtts = norm(btts, 45, 75);
    const sStrong = norm(strongCount, 1, 7);
    const profileScore = 0.20 * sOver + 0.15 * sBtts + 0.10 * sStrong;
    const localScore = Math.max(0, Math.min(10, base + profileScore));
    return {
      score: serverScore != null ? serverScore : localScore,
      baseScore: base,
      breakdown: [
        ...baseBreakdown,
        { label: "Prob. Over 2.5", value: `${over25.toFixed(1)}%`, weight: "20%" },
        { label: "Prob. Gol/Gol", value: `${btts.toFixed(1)}%`, weight: "15%" },
        { label: "Mercati ≥60%", value: `${strongCount}`, weight: "10%" },
      ],
    };
  }

  if (profileId === "exact") {
    const top1 = gundam[0]?.prob ? Number(gundam[0].prob) : 0;
    const top3sum = gundam.slice(0, 3).reduce((acc, g) => acc + Number(g?.prob || 0), 0);
    const sTop = norm(top1, 8, 22);
    const sTop3 = norm(top3sum, 18, 45);
    // Solo gundam (no heatmap) — coerente col calcolo server-side per
    // garantire la stessa numerazione fra MatchCard e MatchPage.
    const profileScore = 0.25 * sTop + 0.20 * sTop3;
    const localScore = Math.max(0, Math.min(10, base + profileScore));
    return {
      score: serverScore != null ? serverScore : localScore,
      baseScore: base,
      breakdown: [
        ...baseBreakdown,
        { label: "Prob. risultato #1", value: `${top1.toFixed(1)}%`, weight: "25%" },
        { label: "Concentrazione Top 3", value: `${top3sum.toFixed(1)}%`, weight: "20%" },
      ],
    };
  }

  return empty;
}

// ─── UI ────────────────────────────────────────────────────────────
export default function PlayProfilePanel({
  activeProfile, onChange,
  enabled = true, onToggleEnabled,
  predictions,
  aggregateScore = null,  // se passato, sostituisce il tachimetro con questo valore (HomePage: media giornaliera)
  aggregateLabel = null,  // testo sotto al label predefinito
  compact = false,
}) {
  const profile = getProfile(activeProfile);
  const [infoOpen, setInfoOpen] = useState(false);
  const computed = useMemo(
    () => computeProfileScore(activeProfile, predictions),
    [activeProfile, predictions],
  );
  // Quando ``aggregateScore`` è fornito, mostriamo quello (caso HomePage:
  // voto medio del giorno sul profilo attivo).
  const score = aggregateScore != null ? Number(aggregateScore) : computed.score;
  const breakdown = aggregateScore != null ? [] : computed.breakdown;

  return (
    <section
      data-testid="play-profile-panel"
      style={{
        background: enabled
          ? `color-mix(in srgb, ${profile.color} 5%, var(--bc-panel, var(--bc-panel, #09090B)))`
          : "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.025)))",
        border: `1px solid ${enabled ? profile.color + "40" : "var(--bc-border, rgba(255,255,255,0.07))"}`,
        boxShadow: enabled ? `0 0 24px -10px ${profile.color}60` : "none",
        padding: "1rem 1.1rem",
        display: "flex",
        flexDirection: "column",
        gap: enabled ? "1rem" : "0.4rem",
      }}
    >
      {/* ─── Header: titolo + master switch + info ─────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", flexWrap: "wrap" }}>
          <span
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.7rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color: enabled ? profile.color : "rgba(255,255,255,0.55)",
              fontWeight: 800,
              textShadow: enabled ? `0 0 10px ${profile.color}80` : "none",
            }}
          >
            Profilo di Gioco
          </span>
          <span
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.6rem",
              letterSpacing: "0.18em",
              color: "rgba(255,255,255,0.45)",
            }}
          >
            · {enabled ? "Riordino dinamico attivo" : "Disattivato"}
          </span>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <button
              data-testid="profile-info-btn"
              onClick={() => setInfoOpen(true)}
              title="Come funzionano i profili e il tachimetro"
              style={{
                background: "transparent",
                border: `1px solid ${enabled ? profile.color + "60" : "rgba(255,255,255,0.15)"}`,
                color: enabled ? profile.color : "rgba(255,255,255,0.7)",
                padding: "0.25rem 0.5rem",
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: "0.65rem",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex", alignItems: "center", gap: "0.3rem",
                transition: "all 0.15s ease",
              }}
            >
              <Info size={11} /> Info
            </button>
            {onToggleEnabled && (
              <button
                data-testid="profile-toggle-enabled"
                aria-pressed={enabled}
                onClick={() => onToggleEnabled(!enabled)}
                title={enabled ? "Disattiva profili (vista classica)" : "Attiva profili di gioco"}
                style={{
                  background: enabled ? `color-mix(in srgb, ${profile.color} 15%, var(--bc-panel, #0A0A0A))` : "var(--bc-panel-elev, rgba(255,255,255,0.04))",
                  border: `1px solid ${enabled ? profile.color : "rgba(255,255,255,0.15)"}`,
                  color: enabled ? profile.color : "rgba(255,255,255,0.55)",
                  padding: "0.25rem 0.5rem 0.25rem 0.4rem",
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: "0.65rem",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "0.3rem",
                  transition: "all 0.15s ease",
                }}
              >
                {enabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                {enabled ? "Attivo" : "Off"}
              </button>
            )}
          </div>
        </div>

        {enabled && (
          <div
            role="tablist"
            data-testid="play-profile-selector"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "0.45rem",
            }}
          >
            {PROFILES.map((p) => {
              const isActive = p.id === activeProfile;
              const Icon = p.icon;
              return (
                <button
                  key={p.id}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`profile-chip-${p.id}`}
                  onClick={() => onChange(p.id)}
                  title={p.desc}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.4rem",
                    padding: "0.55rem 0.4rem",
                    background: isActive
                      ? `color-mix(in srgb, ${p.color} 18%, var(--bc-panel, #0A0A0A))`
                      : "var(--bc-panel-elev, rgba(255,255,255,0.025))",
                    border: `1px solid ${isActive ? p.color : "var(--bc-border, rgba(255,255,255,0.07))"}`,
                    color: isActive ? p.color : "rgba(255,255,255,0.7)",
                    fontFamily: "'Barlow Condensed',sans-serif",
                    fontSize: "0.72rem",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    fontWeight: isActive ? 800 : 600,
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    boxShadow: isActive ? `0 0 12px -4px ${p.color}` : "none",
                  }}
                >
                  <Icon size={12} />
                  <span>{p.short}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Tachimetro + breakdown ──────────────────────────────
          Nascosto se la modalità profili è disattivata. */}
      {enabled && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: compact ? "minmax(140px, 160px) 1fr" : "minmax(180px, 220px) 1fr",
            gap: "1rem",
            alignItems: "center",
          }}
        >
          <Gauge score={score} color={profile.color} compact={compact} testId="profile-gauge" />
          {aggregateScore != null ? (
            <div data-testid="profile-aggregate-info" style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <div style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: "0.95rem", fontWeight: 700, color: "var(--bc-ink, #FFFFFF)",
                letterSpacing: "0.04em",
              }}>
                {profile.label}
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.7rem", letterSpacing: "0.08em",
                color: profile.color,
                textShadow: `0 0 6px ${profile.color}60`,
              }}>
                Voto medio del giorno
              </div>
              <div style={{
                fontFamily: "system-ui, sans-serif",
                fontSize: "0.72rem", color: "rgba(255,255,255,0.65)",
                lineHeight: 1.45,
              }}>
                {aggregateLabel || "Le partite della home sono ordinate dal tachimetro più alto al più basso."}
              </div>
            </div>
          ) : (
            <div
              data-testid="profile-breakdown"
              style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}
            >
              <div
                style={{
                  fontFamily: "'Barlow Condensed',sans-serif",
                  fontSize: compact ? "0.85rem" : "0.95rem",
                  fontWeight: 700,
                  color: "var(--bc-ink, #FFFFFF)",
                  letterSpacing: "0.04em",
                }}
              >
                {profile.label}
              </div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.6rem",
                  letterSpacing: "0.08em",
                  color: "rgba(255,255,255,0.45)",
                  marginBottom: "0.15rem",
                }}
              >
                {profile.desc}
              </div>
              {breakdown.map((b) => (
                <div
                  key={b.label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.12rem 0",
                    borderBottom: "1px dashed var(--bc-border, rgba(255,255,255,0.07))",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontSize: "0.72rem",
                      letterSpacing: "0.08em",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    {b.label}
                  </span>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: profile.color,
                      textShadow: `0 0 6px ${profile.color}80`,
                    }}
                  >
                    {b.value}
                  </span>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: "0.55rem",
                      color: "rgba(255,255,255,0.35)",
                      width: 28,
                      textAlign: "right",
                    }}
                  >
                    {b.weight}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {infoOpen && <InfoModal onClose={() => setInfoOpen(false)} />}
    </section>
  );
}

// ─── SVG semi-circular gauge with neon glow ──────────────────────
// Bugfix: ``large-arc-flag`` deve essere SEMPRE 0 perché il tracciato
// resta sempre nella semicirconferenza superiore (≤180°). Prima era
// impostato a 1 quando ``score > 5``, e il browser disegnava l'arco
// lungo che girava in cerchio sopra al pannello.
function Gauge({ score, color, compact = false, testId }) {
  const s = Math.max(0, Math.min(10, Number(score) || 0));
  const W = compact ? 150 : 200;
  const H = compact ? 88 : 110;
  const cx = W / 2;
  const cy = H - 10;
  const r = compact ? 60 : 80;

  // Angolo del needle: 180° (sinistra orizzontale) → 360°/0° (destra orizzontale).
  // In SVG l'asse Y cresce verso il basso, quindi sin(angle) negativo = sopra cy.
  const angle = Math.PI + (s / 10) * Math.PI;
  const tipX = cx + r * Math.cos(angle);
  const tipY = cy + r * Math.sin(angle);
  const arcStart = `${cx - r},${cy}`;
  const arcEnd = `${cx + r},${cy}`;

  const label = s >= 8 ? "ECCELLENTE"
              : s >= 6 ? "BUONA"
              : s >= 4 ? "MEDIA"
              : s >= 2 ? "BASSA"
              : "SCARSA";
  const gradId = `gauge-grad-${color.replace("#", "")}`;
  const glowId = `gauge-glow-${color.replace("#", "")}`;

  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FF6FD8" />
            <stop offset="50%" stopColor="#F6C643" />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Sfondo arco (sempre 180°, large-arc-flag=0) */}
        <path
          d={`M ${arcStart} A ${r} ${r} 0 0 1 ${arcEnd}`}
          fill="none"
          stroke="var(--bc-border, rgba(255,255,255,0.07))"
          strokeWidth={compact ? 8 : 10}
          strokeLinecap="round"
        />

        {/* Tick marks */}
        {Array.from({ length: 11 }).map((_, i) => {
          const a = Math.PI + (i / 10) * Math.PI;
          const inner = r - (compact ? 8 : 10);
          const outer = r + (i % 5 === 0 ? 6 : 3);
          const x1 = cx + inner * Math.cos(a);
          const y1 = cy + inner * Math.sin(a);
          const x2 = cx + outer * Math.cos(a);
          const y2 = cy + outer * Math.sin(a);
          return (
            <line
              key={i}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={i % 5 === 0 ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.2)"}
              strokeWidth={i % 5 === 0 ? 1.4 : 0.8}
            />
          );
        })}

        {/* Arco attivo fino al punteggio (large-arc-flag SEMPRE 0) */}
        <path
          d={`M ${arcStart} A ${r} ${r} 0 0 1 ${tipX} ${tipY}`}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={compact ? 8 : 10}
          strokeLinecap="round"
          filter={`url(#${glowId})`}
          style={{ transition: "all 0.6s ease-out" }}
        />

        {/* Lancetta */}
        <line
          x1={cx} y1={cy}
          x2={tipX} y2={tipY}
          stroke={color}
          strokeWidth={compact ? 2.2 : 3}
          strokeLinecap="round"
          filter={`url(#${glowId})`}
          style={{ transition: "all 0.6s ease-out" }}
        />
        <circle cx={cx} cy={cy} r={compact ? 4.5 : 6} fill="#020202" stroke={color} strokeWidth={2} />

        {/* Score */}
        <text
          x={cx} y={cy - (compact ? 14 : 18)}
          textAnchor="middle"
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: compact ? 22 : 28,
            fontWeight: 800,
            fill: color,
            filter: `drop-shadow(0 0 6px ${color})`,
          }}
        >
          {s.toFixed(1)}
        </text>
        <text
          x={cx} y={cy - (compact ? 2 : 4)}
          textAnchor="middle"
          style={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: 9,
            fill: "rgba(255,255,255,0.45)",
            letterSpacing: 1.5,
          }}
        >
          /10
        </text>
      </svg>
      <span
        data-testid="profile-gauge-label"
        style={{
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: compact ? "0.6rem" : "0.7rem",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color,
          fontWeight: 800,
          textShadow: `0 0 8px ${color}80`,
          marginTop: "-0.2rem",
        }}
      >
        Affidabilità · {label}
      </span>
    </div>
  );
}

// ─── Info modal: spiega profili e tachimetro ──────────────────────
// Render via React Portal per evitare di restare intrappolati nello
// stacking context creato da ``.bc-cinematic > *`` (filter:blur(0) in
// keyframes crea un nuovo stacking context anche dopo il completamento
// dell'animazione, schiacciando l'``z-index: 100`` del modal).
function InfoModal({ onClose }) {
  const modalContent = (
    <div
      data-testid="profile-info-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--bc-panel, rgba(0,0,0,0.75))",
        backdropFilter: "blur(4px)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bc-info-modal-scroll"
        style={{
          background: "var(--bc-panel, var(--bc-panel, #09090B))",
          border: "1px solid rgba(167,139,250,0.4)",
          boxShadow: "0 0 40px -10px rgba(167,139,250,0.5)",
          padding: "1.1rem 1.2rem",
          maxWidth: 880,
          width: "100%",
          maxHeight: "88vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "0.8rem",
          scrollbarWidth: "thin",
          scrollbarColor: "#A78BFA #1A1A1F",
        }}
      >
        <div style={{
          position: "sticky", top: 0, zIndex: 2,
          background: "var(--bc-panel, var(--bc-panel, #09090B))",
          paddingBottom: "0.4rem",
          marginBottom: "-0.2rem",
          display: "flex", alignItems: "center", gap: "0.6rem",
          borderBottom: "1px solid rgba(167,139,250,0.15)",
        }}>
          <Info size={15} style={{ color: "#A78BFA" }} />
          <span
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.85rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "#A78BFA",
              fontWeight: 800,
            }}
          >
            Come funzionano i Profili di Gioco
          </span>
          <button
            data-testid="profile-info-close"
            onClick={onClose}
            style={{
              marginLeft: "auto",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "rgba(255,255,255,0.75)",
              padding: "0.22rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
            title="Chiudi"
          >
            <X size={13} />
          </button>
        </div>

        <p
          style={{
            margin: 0,
            fontFamily: "system-ui, sans-serif",
            fontSize: "0.75rem",
            color: "rgba(255,255,255,0.78)",
            lineHeight: 1.5,
          }}
        >
          I <strong>Profili di Gioco</strong> riordinano i pannelli della pagina partita e
          ordinano le partite della home in base al tuo stile di scommessa. Il{" "}
          <strong>Tachimetro</strong> mostra un punteggio da 1 a 10 che indica quanto la
          partita è adatta al profilo selezionato.
        </p>

        <div
          style={{
            background: "rgba(167,139,250,0.05)",
            border: "1px solid rgba(167,139,250,0.25)",
            padding: "0.65rem 0.8rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.3rem",
          }}
        >
          <span
            style={{
              fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.68rem",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#A78BFA",
              fontWeight: 800,
            }}
          >
            Tachimetro · come si calcola
          </span>
          <p style={{ margin: 0, fontSize: "0.72rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>
            Il <strong>55%</strong> del punteggio è una <strong>base globale</strong> identica per tutti i profili e
            misura quanto la partita è solida:{" "}
            <span style={{ color: "#22FFA3", fontWeight: 700 }}>40%</span> affidabilità globale
            (<code style={{ fontSize: "0.7rem" }}>affidabilita.score</code>) +{" "}
            <span style={{ color: "#22FFA3", fontWeight: 700 }}>15%</span> convergenza modelli
            (Poisson + ELO + Forma).
          </p>
          <p style={{ margin: 0, fontSize: "0.72rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.5 }}>
            Il restante <strong>45%</strong> è specifico del profilo (vedi sotto).{" "}
            <em>Una partita con bassa affidabilità globale non può mai superare ~5.5/10 in nessun profilo.</em>
          </p>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "0.55rem",
        }}>
          {PROFILES.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                data-testid={`profile-info-${p.id}`}
                style={{
                  background: `color-mix(in srgb, ${p.color} 5%, var(--bc-panel, #0A0A0A))`,
                  border: `1px solid ${p.color}40`,
                  padding: "0.65rem 0.75rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.35rem",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <Icon size={12} style={{ color: p.color }} />
                  <span
                    style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontSize: "0.78rem",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: p.color,
                      fontWeight: 800,
                      textShadow: `0 0 6px ${p.color}60`,
                    }}
                  >
                    {p.label}
                  </span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: "0.55rem",
                      color: "rgba(255,255,255,0.45)",
                      letterSpacing: "0.12em",
                    }}
                  >
                    {p.short}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: "0.68rem", color: "rgba(255,255,255,0.78)", lineHeight: 1.4 }}>
                  <strong style={{ color: "rgba(255,255,255,0.92)" }}>Obiettivo:</strong> {p.explain.goal}
                </p>
                <p style={{ margin: 0, fontSize: "0.66rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.4 }}>
                  <strong style={{ color: "rgba(255,255,255,0.85)" }}>Pannelli:</strong> {p.explain.panels}
                </p>
                <div>
                  <div
                    style={{
                      fontFamily: "'Barlow Condensed',sans-serif",
                      fontSize: "0.56rem",
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: p.color,
                      fontWeight: 700,
                      marginBottom: "0.15rem",
                    }}
                  >
                    Formula
                  </div>
                  <ul style={{ margin: 0, paddingLeft: "0.95rem", fontSize: "0.64rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.45 }}>
                    {p.explain.formula.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <p
          style={{
            margin: 0,
            fontSize: "0.66rem",
            color: "rgba(255,255,255,0.5)",
            lineHeight: 1.4,
            paddingBottom: "0.6rem",
          }}
        >
          Puoi disattivare la modalità Profili dal toggle in alto a destra del pannello:
          in quel caso la home e la pagina partita tornano alla visualizzazione classica.
        </p>
      </div>
    </div>
  );
  // Portal su document.body: scavalca qualsiasi stacking context dei
  // genitori (es. ``.bc-cinematic > *`` con filter:blur(0)).
  if (typeof document !== "undefined" && document.body) {
    return createPortal(modalContent, document.body);
  }
  return modalContent;
}
